// Turning several events' summary counts into the comparison report: a row per
// count, a column per event.
//
// The backend (core/event_summaries.py) serves plain `label -> count` tallies and
// deliberately no ordering — the display order of every breakdown is an option
// list in constants.js, and duplicating those lists server-side is exactly the
// desync CLAUDE.md warns about. So this module owns the report's *shape*: which
// rows there are, what they're called, and what order they come in.
//
// It is shared because the screen and the spreadsheet must be the same report:
// components/EventComparisonDialog.jsx renders these sections and
// exportEventComparison() writes the same ones out.

import {
  ATTENDANCE_ROLES,
  LOCATION_TYPES,
  INCIDENT_TYPES,
  INCIDENT_DETAIL_TYPES,
  INCIDENT_STATUSES,
  RESPONSE_TEAM_DEPLOYMENTS,
  MOUNTAIN_SAFETY_DEPLOYMENTS,
  DISPOSAL_TRANSPORTS,
  DISPOSAL_DESTINATIONS,
  ADVISED_TO_RETIRE_OPTIONS,
  MEDICAL_INCIDENT_TYPES,
  locationSummaryOrder,
} from "./constants";

// The labels the single-event summary gives a blank value, which the API sends as
// "" (deciding what to call nothing is a display question). Kept identical to the
// `emptyLabel`s in pages/Events.jsx so the two reports read the same.
const NO_ROLE = "No event role";
const NO_LOCATION_TYPE = "No type";
const NO_RACE = "No race";
const NO_INCIDENT_TYPE = "No type";
const NO_DETAIL_TYPE = "No detail type";
const NO_SUBTYPE = "No subtype";
const NO_STATUS = "No status";

// Whether every selected event is the same event in different years. The
// name-specific breakdowns (an entry list's race names, an event's individual
// locations) only line up when it is: across different events they'd be one row
// per event with a count in one column, which says nothing. Compared
// case-insensitively and trimmed, since the name is free text typed each year.
export const sameEventName = (events) =>
  new Set((events || []).map(e => (e.name || "").trim().toLowerCase())).size === 1;

// An event's dates as the Events page writes them — the column's second line, and
// the sheet's first row.
export const eventDateRange = (e) => {
  if (!e?.start_date) return "—";
  return e.end_date && e.end_date !== e.start_date
    ? `${e.start_date} → ${e.end_date}`
    : e.start_date;
};

export const eventColumnLabel = (e) => e?.label_long || e?.name || `Event ${e?.id}`;

// Incidents per 1,000 running hours, to two decimal places — or null when the
// event has no running hours recorded, which is "unknown", never a rate of zero.
// The one rule for both reports: this one, and the single-event summary in
// pages/Events.jsx. Derived here rather than served, since both of its inputs
// already are.
export const incidentsPer1000Hours = (incidents, hours) => {
  const h = Number(hours);
  if (hours == null || hours === "" || !(h > 0)) return null;
  return Math.round((incidents / h) * 1000 * 100) / 100;
};

// The medical incidents (Medical Major + Minor) out of an incident-type tally,
// for the medical rate beside the overall one.
const medicalCount = (byType) =>
  MEDICAL_INCIDENT_TYPES.reduce((n, t) => n + ((byType || {})[t] || 0), 0);

const countFor = (map, label, emptyLabel) =>
  (map || {})[label === emptyLabel ? "" : label] || 0;

// One breakdown as rows: every label any of the selected events recorded, in the
// order the single-event summary uses (the entries of `order` first, in that
// order, then the rest alphabetically — or by total count when there is no list
// to go by). Mirrors tally() in pages/Events.jsx.
//
// A label nothing in the selection recorded is not a row at all. What *is* a row
// is a label only some of them recorded: the others read 0, which is the
// comparison — an aid station that moved, a drug given one year and not the next.
//
// `include` is the exception, and matches `tally`'s option of the same name in
// pages/Events.jsx: labels from a *fixed* option list that are listed whether or
// not anything was counted against them, because there the absence is the answer.
const unionRows = (maps, { order, include, emptyLabel = "—", level = 0 } = {}) => {
  const totals = new Map();
  (include || []).forEach(label => totals.set(label, 0));
  maps.forEach(m => Object.entries(m || {}).forEach(([key, n]) => {
    const label = key === "" ? emptyLabel : key;
    totals.set(label, (totals.get(label) || 0) + n);
  }));
  const labels = [...totals.keys()];
  if (order) {
    const rank = (l) => { const i = order.indexOf(l); return i === -1 ? order.length : i; };
    labels.sort((a, b) => (rank(a) - rank(b)) || a.localeCompare(b));
  } else {
    labels.sort((a, b) => (totals.get(b) - totals.get(a)) || a.localeCompare(b));
  }
  return labels.map(label => ({
    label,
    level,
    values: maps.map(m => countFor(m, label, emptyLabel)),
  }));
};

// A row read straight off each event's summary rather than out of a breakdown.
// `blank` keeps a missing value as null (shown "—", an empty cell in the sheet)
// rather than 0, for figures where nothing recorded isn't the same as none.
const scalarRow = (label, summaries, pick, { emphasis = false, blank = false } = {}) => ({
  label,
  level: 0,
  emphasis,
  values: summaries.map(s => (blank ? (pick(s) ?? null) : (pick(s) || 0))),
});

const mapsOf = (summaries, key) => summaries.map(s => s[key] || {});

// The sub-type rows belonging under each detail type, nested as the single-event
// summary nests them. Sub-types are ordered by count rather than by a list: the
// per-type lists in INCIDENT_SUBTYPES are long, and the summary doesn't order them
// either.
const detailTypeRows = (summaries) =>
  unionRows(mapsOf(summaries, "incidents_by_detail_type"),
            { order: INCIDENT_DETAIL_TYPES, emptyLabel: NO_DETAIL_TYPE })
    .flatMap(parent => [
      parent,
      ...unionRows(
        summaries.map(s => (s.incidents_by_detail_subtype || {})[
          parent.label === NO_DETAIL_TYPE ? "" : parent.label] || {}),
        { emptyLabel: NO_SUBTYPE, level: 1 }),
    ]);

// Why a report of mixed events is shorter, in the words the dialog shows.
export const MIXED_EVENTS_NOTE =
  "Events with different names are compared only on the counts they share: the "
  + "entry list is totalled rather than broken down by race, and incidents are "
  + "grouped by location type rather than by individual location.";

// The whole report. `summaries` is the API's response, already ordered by name
// then year — that ordering is the column order, and the caller must not reorder
// it, since every row's `values` are positional.
export const buildSections = (summaries) => {
  if (!summaries?.length) return [];
  const events = summaries.map(s => s.event);
  const sameName = sameEventName(events);

  const sections = [
    {
      title: "Personnel",
      rows: [
        scalarRow("Total", summaries, s => s.personnel_total, { emphasis: true }),
        ...unionRows(mapsOf(summaries, "personnel_by_role"),
                     { order: ATTENDANCE_ROLES, emptyLabel: NO_ROLE }),
      ],
    },
    {
      title: "Locations",
      rows: [
        scalarRow("Total", summaries, s => s.locations_total, { emphasis: true }),
        ...unionRows(mapsOf(summaries, "locations_by_type"),
                     { order: LOCATION_TYPES, emptyLabel: NO_LOCATION_TYPE }),
      ],
    },
    {
      title: "Participants",
      rows: [
        scalarRow("Total", summaries, s => s.participants_total, { emphasis: true }),
        // Race names are one event's own vocabulary — only comparable between
        // years of that event.
        ...(sameName
          ? unionRows(mapsOf(summaries, "participants_by_race"), { emptyLabel: NO_RACE })
          : []),
      ],
    },
    {
      title: "Incidents",
      rows: [
        scalarRow("Total", summaries, s => s.incidents_total, { emphasis: true }),
        // Entered on the event, so an event without it reads "—" for both rows:
        // a rate can only be compared where the hours were recorded.
        scalarRow("Running hours", summaries, s => s.event.total_running_hours,
                  { blank: true }),
        scalarRow("All incidents per 1,000 running hours", summaries,
                  s => incidentsPer1000Hours(s.incidents_total, s.event.total_running_hours),
                  { blank: true }),
        scalarRow("Medical incidents per 1,000 running hours", summaries,
                  s => incidentsPer1000Hours(medicalCount(s.incidents_by_type),
                                             s.event.total_running_hours),
                  { blank: true }),
      ],
    },
    {
      title: "Incidents by type",
      rows: unionRows(mapsOf(summaries, "incidents_by_type"),
                      { order: INCIDENT_TYPES, emptyLabel: NO_INCIDENT_TYPE }),
    },
    {
      title: "Incidents by detail type and sub-type",
      rows: detailTypeRows(summaries),
    },
    {
      title: "Incidents by location type",
      rows: unionRows(mapsOf(summaries, "incidents_by_location_type"),
                      { order: LOCATION_TYPES, emptyLabel: NO_LOCATION_TYPE }),
    },
    // Individual locations, for the same reason as race names: "Aid Station 3"
    // means one place across years of one event and nothing at all across two.
    // Kept even between years, where an aid station that moved shows as two rows
    // with a zero each — which is the thing worth seeing.
    // Listed in the order the Event Locations page lists the places themselves —
    // by type, then name, with Other last — across every column's locations, so
    // an aid station one year moved sits with the other aid stations rather than
    // wherever its count put it.
    ...(sameName ? [{
      title: "Incidents by location",
      rows: unionRows(mapsOf(summaries, "incidents_by_location"),
                      { order: locationSummaryOrder(
                          summaries.flatMap(s => s.locations || [])) }),
    }] : []),
    // The two deployment breakdowns list every type, zero included: "the Response
    // Team wasn't called out" is a finding of the report, and a row the count
    // rule would have dropped is the only way to say it. Their sections are
    // therefore never the empty ones filtered out below.
    {
      title: "Incidents by Response Team deployment",
      rows: unionRows(mapsOf(summaries, "incidents_by_response_team"),
                      { order: RESPONSE_TEAM_DEPLOYMENTS,
                        include: RESPONSE_TEAM_DEPLOYMENTS }),
    },
    {
      title: "Incidents by Mountain Safety deployment",
      rows: unionRows(mapsOf(summaries, "incidents_by_mountain_safety"),
                      { order: MOUNTAIN_SAFETY_DEPLOYMENTS,
                        include: MOUNTAIN_SAFETY_DEPLOYMENTS }),
    },
    // Every option listed, zero included, for the same reason as the deployments
    // above: "no helicopter all weekend" is worth being able to read.
    {
      title: "Incidents by disposal transport",
      rows: unionRows(mapsOf(summaries, "incidents_by_disposal_transport"),
                      { order: DISPOSAL_TRANSPORTS, include: DISPOSAL_TRANSPORTS }),
    },
    {
      title: "Incidents by disposal destination",
      rows: unionRows(mapsOf(summaries, "incidents_by_disposal_destination"),
                      { order: DISPOSAL_DESTINATIONS, include: DISPOSAL_DESTINATIONS }),
    },
    {
      title: "Incidents by status",
      rows: unionRows(mapsOf(summaries, "incidents_by_status"),
                      { order: INCIDENT_STATUSES, emptyLabel: NO_STATUS }),
    },
    {
      title: "Clinical",
      rows: [
        scalarRow("Advised to retire", summaries, s => s.advised_to_retire),
        // What they did about it, nested under the total. Only the outcomes some
        // event recorded — including the legacy "Yes" from the tickbox days.
        ...unionRows(mapsOf(summaries, "advised_to_retire_by_outcome"),
                     { order: ADVISED_TO_RETIRE_OPTIONS, level: 1 }),
        // The same test as the Incidents page's "Pending clinical review" filter,
        // applied server-side (core/incident_review.py).
        scalarRow("Pending clinical review", summaries, s => s.pending_clinical_review),
      ],
    },
    {
      title: "Drugs given",
      // Amounts, not administrations — two packs on one incident read as 2.
      rows: unionRows(mapsOf(summaries, "drugs_given")),
    },
  ];

  // A breakdown nothing in the selection recorded drops out whole, heading and
  // all: a report of eleven empty headings hides the counts that matter.
  return sections.filter(s => s.rows.length);
};
